import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { Coffee } from "../types";
import { isTea, kindLabel } from "../types";
import { FlavorTags } from "./FlavorTags";
import { GradeBadge } from "./GradeBadge";
import { primaryPhotoUrl, photoUrl } from "../lib/photos";

interface Props {
  coffee: Coffee;
  onUse?: (id: number) => void;
}

// Cap how tall a card photo may render, relative to the card width, so one long
// portrait bag shot doesn't tower over the grid — or, in the swipe carousel,
// stretch every slide to the tallest image's height. Anything taller than the
// cap is cropped top-anchored (label stays visible); shorter images show in full.
const MAX_H_RATIO = 1.5; // height ≤ 1.5 × width  (3:2 portrait)

function CardPhoto({ coffee }: { coffee: Coffee }) {
  const navigate = useNavigate();
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const photos = coffee.photos;

  // Natural height/width ratio per photo, learned as each <img> loads.
  const [ratios, setRatios] = useState<Record<number, number>>({});
  const noteRatio = (id: number, img: HTMLImageElement) => {
    if (!img.naturalWidth) return;
    const r = img.naturalHeight / img.naturalWidth;
    setRatios((cur) => (cur[id] === r ? cur : { ...cur, [id]: r }));
  };
  // Single-photo (or photo_url fallback) tallness, tracked separately since the
  // fallback has no photo id to key on.
  const [oneTall, setOneTall] = useState(false);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el || photos.length <= 1) return;
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const w = el.clientWidth;
        if (w === 0) return;
        const i = Math.round(el.scrollLeft / w);
        if (i !== activeIdx && i >= 0 && i < photos.length) setActiveIdx(i);
      });
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      el.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(raf);
    };
  }, [photos.length, activeIdx]);

  // Tap-vs-swipe: only navigate if the pointer didn't move much horizontally.
  const downX = useRef<number | null>(null);
  const onPointerDown = (e: React.PointerEvent) => { downX.current = e.clientX; };
  const onClickPhoto = (e: React.MouseEvent) => {
    const x = downX.current;
    downX.current = null;
    if (x !== null && Math.abs(e.clientX - x) > 8) return;
    navigate(`/coffee/${coffee.id}`);
  };

  if (photos.length <= 1) {
    const url = primaryPhotoUrl(coffee);
    return (
      <div
        className="bg-paper-100 border border-paper-200 overflow-hidden relative cursor-pointer"
        onClick={() => navigate(`/coffee/${coffee.id}`)}
      >
        {url ? (
          <img
            src={url}
            alt=""
            onLoad={(e) => {
              const im = e.currentTarget;
              setOneTall(im.naturalWidth ? im.naturalHeight / im.naturalWidth > MAX_H_RATIO : false);
            }}
            className={`w-full block transition group-hover:opacity-90 ${oneTall ? "object-cover object-top" : "h-auto"}`}
            style={oneTall ? { aspectRatio: `1 / ${MAX_H_RATIO}` } : undefined}
          />
        ) : (
          <div className="aspect-[4/5] w-full flex items-center justify-center text-paper-300 text-6xl font-display">
            {coffee.variety.slice(0, 1).toUpperCase()}
          </div>
        )}
      </div>
    );
  }

  // The carousel frame follows the PRIMARY photo's shape (capped at 3:2), not the
  // tallest sibling — a landscape main image gets a landscape frame instead of
  // being stranded in empty space because photo #3 happens to be a tall portrait.
  // Every slide then covers that frame (absolute inset so the % height resolves
  // against the aspect-ratio box; object-top keeps the label in view).
  const primaryRatio = photos[0] ? ratios[photos[0].id] : undefined;
  const frameRatio = Math.min(MAX_H_RATIO, primaryRatio ?? MAX_H_RATIO);

  return (
    <div className="relative">
      <div
        ref={scrollerRef}
        className="border border-paper-200 bg-paper-100 overflow-x-auto overflow-y-hidden flex snap-x snap-mandatory cursor-pointer"
        style={{ scrollbarWidth: "none" }}
        onPointerDown={onPointerDown}
        onClick={onClickPhoto}
      >
        {photos.map((p) => (
          <div key={p.id} className="snap-start shrink-0 w-full relative" style={{ aspectRatio: `1 / ${frameRatio}` }}>
            <img
              src={photoUrl(p.r2_key)!}
              alt=""
              onLoad={(e) => noteRatio(p.id, e.currentTarget)}
              className="absolute inset-0 w-full h-full object-cover object-top block select-none"
              draggable={false}
            />
          </div>
        ))}
      </div>
      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5 px-2 py-1 bg-paper-50/80 backdrop-blur pointer-events-none">
        {photos.map((_, i) => (
          <span key={i} className={`block w-1.5 h-1.5 rounded-full ${i === activeIdx ? "bg-ink" : "bg-paper-300"}`} />
        ))}
      </div>
    </div>
  );
}

export function CoffeeCard({ coffee, onUse }: Props) {
  const tea = isTea(coffee.kind);
  const low = !tea && coffee.portions <= 2;
  const stockLabel = tea
    ? `${coffee.portions} bottle${coffee.portions === 1 ? "" : "s"}`
    : `${coffee.portions} × ${coffee.grams}g`;
  const subtitle = tea
    ? [coffee.roaster, coffee.roaster_city].filter(Boolean).join(" · ")
    : `${[coffee.roaster, coffee.roaster_city].filter(Boolean).join(" · ")} · ${coffee.process}`;
  return (
    <div className="group flex flex-col">
      <CardPhoto coffee={coffee} />
      <Link to={`/coffee/${coffee.id}`} className="pt-3 pb-1 flex flex-col gap-1">
        <div className="eyebrow flex items-center justify-between gap-2">
          <span>{coffee.country}{coffee.region ? ` · ${coffee.region}` : ""}</span>
          {coffee.kind && coffee.kind !== "coffee" ? (
            <span className="text-[9px] tracking-widest text-paper-500">{kindLabel(coffee.kind).toUpperCase()}</span>
          ) : null}
        </div>
        <h3 className="font-display text-lg leading-snug">{coffee.variety}</h3>
        <p className="text-xs text-paper-600">{subtitle}</p>

        <div className="mt-1.5"><GradeBadge grade={coffee.grade} score={coffee.award_score} /></div>
        <div className="mt-0.5"><FlavorTags tags={coffee.flavor_tags.slice(0, 5)} /></div>

        <div className="rule mt-3 pt-2 flex items-center justify-between">
          <span className={`text-xs tabular-nums ${low ? "text-red-700" : "text-paper-600"}`}>
            {stockLabel}
          </span>
          {onUse ? (
            <button
              onClick={(e) => { e.preventDefault(); onUse(coffee.id); }}
              className="text-[10px] uppercase tracking-widest text-paper-500 hover:text-ink"
              disabled={coffee.portions === 0}
            >
              {tea ? "Use one bottle →" : "Use one →"}
            </button>
          ) : null}
        </div>
      </Link>
    </div>
  );
}
